import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import clsx from 'clsx';
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  CircleDashed,
  Pill,
  Stethoscope,
  Users,
} from 'lucide-react';
import { reportsService } from '../../services/reports';
import { DateRangePicker, TimelineChart, PieChart, DataTable } from '../../components/reports';
import { CHART_COLORS, STATUS_COLORS } from '../../components/reports/chartTheme';
import { PageHeader, KpiCard } from '@/components/ui';

export function PharmacistReports() {
  const { t, i18n } = useTranslation();
  const numberLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';
  const fmt = (n: number | undefined) => (n ?? 0).toLocaleString(numberLocale);
  const [dateRange, setDateRange] = useState({
    fromDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    toDate: format(new Date(), 'yyyy-MM-dd'),
  });
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  const { data: overview } = useQuery({
    queryKey: ['pharmacist-overview', dateRange],
    queryFn: () => reportsService.getPharmacistOverview(dateRange),
  });

  const { data: timeline } = useQuery({
    queryKey: ['pharmacist-timeline', dateRange, groupBy],
    queryFn: () => reportsService.getPharmacistTimeline({ ...dateRange, groupBy }),
  });

  const { data: topDrugs } = useQuery({
    queryKey: ['pharmacist-top-drugs', dateRange],
    queryFn: () => reportsService.getPharmacistTopDrugs({ ...dateRange, limit: 10 }),
  });

  const { data: substitutionRate } = useQuery({
    queryKey: ['pharmacist-substitution-rate', dateRange],
    queryFn: () => reportsService.getPharmacistSubstitutionRate(dateRange),
  });

  const { data: partialRate } = useQuery({
    queryKey: ['pharmacist-partial-rate', dateRange],
    queryFn: () => reportsService.getPharmacistPartialRate(dateRange),
  });

  const handleDateChange = (fromDate: string, toDate: string) => {
    setDateRange({ fromDate, toDate });
  };

  const groupByLabels: Record<string, string> = {
    day: t('reports.day'),
    week: t('reports.week'),
    month: t('reports.month'),
  };

  const rate = Math.min(100, Math.max(0, substitutionRate?.substitutionRate || 0));

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('reports.title')}
        description={t('pharmacist.reports.description')}
        actions={
          <DateRangePicker
            fromDate={dateRange.fromDate}
            toDate={dateRange.toDate}
            onChange={handleDateChange}
          />
        }
      />

      {/* Overview Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label={t('reports.totalFulfillments')}
          value={fmt(overview?.totalFulfillments)}
          icon={ClipboardCheck}
          tone="primary"
        />
        <KpiCard
          label={t('reports.itemsDispensed')}
          value={fmt(overview?.totalItemsDispensed)}
          icon={Pill}
          tone="navy"
        />
        <KpiCard
          label={t('reports.complete')}
          value={fmt(overview?.completeFulfillments)}
          icon={CheckCircle2}
          tone="success"
        />
        <KpiCard
          label={t('reports.partial')}
          value={fmt(overview?.partialFulfillments)}
          icon={CircleDashed}
          tone="warning"
        />
        <KpiCard
          label={t('reports.uniquePatients')}
          value={fmt(overview?.uniquePatients)}
          icon={Users}
          tone="gray"
        />
        <KpiCard
          label={t('reports.uniquePrescribers')}
          value={fmt(overview?.uniquePrescribers)}
          icon={Stethoscope}
          tone="gray"
        />
      </div>

      {/* Timeline */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600">{t('reports.groupBy')}</span>
          <div className="inline-flex rounded-lg bg-gray-100 p-1" role="group" aria-label={t('reports.groupBy')}>
            {(['day', 'week', 'month'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setGroupBy(option)}
                aria-pressed={groupBy === option}
                className={clsx(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  groupBy === option
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                {groupByLabels[option]}
              </button>
            ))}
          </div>
        </div>
        <TimelineChart
          data={timeline || []}
          title={t('reports.fulfillmentsOverTime')}
          color={CHART_COLORS.primary}
          groupBy={groupBy}
        />
      </div>

      {/* Rates Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <PieChart
          data={[
            {
              name: t('reports.complete'),
              value: partialRate?.completeFulfillments || 0,
              color: STATUS_COLORS.FULFILLED,
            },
            {
              name: t('reports.partial'),
              value: partialRate?.partialFulfillments || 0,
              color: STATUS_COLORS.PARTIALLY_FULFILLED,
            },
          ]}
          title={t('reports.completionRate')}
        />

        <div className="card min-w-0 p-5">
          <h3 className="card-title mb-4">{t('reports.substitutionStats')}</h3>
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-gray-600">{t('reports.totalItemsDispensed')}</dt>
              <dd className="font-medium tabular-nums text-gray-900">{fmt(substitutionRate?.totalItems)}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-gray-600">{t('reports.itemsSubstituted')}</dt>
              <dd className="font-medium tabular-nums text-gray-900">{fmt(substitutionRate?.substitutedItems)}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-gray-600">{t('reports.substitutionRate')}</dt>
              <dd className="font-semibold tabular-nums text-primary-700">{rate}%</dd>
            </div>
          </dl>
          <div
            className="mt-3 h-2.5 overflow-hidden rounded-full bg-gray-100"
            role="progressbar"
            aria-valuenow={rate}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('reports.substitutionRate')}
          >
            <div className="h-full rounded-full bg-primary-600" style={{ width: `${rate}%` }} />
          </div>

          {substitutionRate?.topSubstitutions && substitutionRate.topSubstitutions.length > 0 && (
            <div className="mt-6 border-t border-gray-100 pt-4">
              <h4 className="section-title mb-3">{t('reports.topSubstitutions')}</h4>
              <ul className="space-y-2">
                {substitutionRate.topSubstitutions.slice(0, 5).map((sub, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span className="flex min-w-0 items-center gap-1.5 text-gray-600">
                      <span className="truncate">{sub.originalDrug}</span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-gray-400 rtl-flip" />
                      <span className="truncate font-medium text-gray-900">{sub.substitutedDrug}</span>
                    </span>
                    <span className="badge-gray shrink-0 tabular-nums">{sub.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Top Drugs */}
      <DataTable
        title={t('reports.mostDispensedDrugs')}
        data={topDrugs || []}
        columns={[
          { key: 'tradeName', header: t('drug.tradeName') },
          { key: 'dci', header: t('drug.dci') },
          { key: 'atcCode', header: t('drug.atcCode') },
          {
            key: 'totalQuantity',
            header: t('reports.totalQty'),
            render: (row) => fmt(row.totalQuantity),
          },
          {
            key: 'prescriptionCount',
            header: t('reports.rxCount'),
            render: (row) => fmt(row.prescriptionCount),
          },
        ]}
        maxRows={10}
      />
    </div>
  );
}
